/**
 * Product Media Slider - Componentes JavaScript extraídos del tema Focal
 * Incluye todos los componentes necesarios para el slider de media de producto
 */

// ===== FUNCIONES UTILITARIAS =====
function triggerEvent(element, name, data = {}) {
  element.dispatchEvent(new CustomEvent(name, {
    bubbles: true,
    detail: data
  }));
}

function getSiblings(element, filter, includeSelf = false) {
  let siblings = [];
  let currentElement = element;
  while (currentElement = currentElement.previousElementSibling) {
    if (!filter || currentElement.matches(filter)) {
      siblings.push(currentElement);
    }
  }
  if (includeSelf) {
    siblings.push(element);
  }
  currentElement = element;
  while (currentElement = currentElement.nextElementSibling) {
    if (!filter || currentElement.matches(filter)) {
      siblings.push(currentElement);
    }
  }
  return siblings;
}

// ===== DELEGATE SIMPLE (definir antes de usarlo) =====
class Delegate {
  constructor(element) {
    this.element = element;
    this.listeners = new Map();
  }
  
  on(event, selector, handler) {
    const wrappedHandler = (e) => {
      // Buscar el elemento que coincide con el selector, incluyendo elementos hijos
      let target = e.target;
      while (target && target !== this.element) {
        if (target.matches && target.matches(selector)) {
          handler(e, target);
          return;
        }
        target = target.parentElement;
      }
    };
    
    this.element.addEventListener(event, wrappedHandler);
    
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push({ selector, handler, wrappedHandler });
  }
  
  off(event, selector, handler) {
    if (!event) {
      this.destroy();
      return this;
    }
    
    const eventListeners = this.listeners.get(event);
    if (!eventListeners) return this;
    
    for (let i = eventListeners.length - 1; i >= 0; i--) {
      const listener = eventListeners[i];
      if ((!selector || listener.selector === selector) && 
          (!handler || listener.handler === handler)) {
        this.element.removeEventListener(event, listener.wrappedHandler);
        eventListeners.splice(i, 1);
      }
    }
    
    if (eventListeners.length === 0) {
      this.listeners.delete(event);
    }
    
    return this;
  }
  
  destroy() {
    this.listeners.forEach((listeners, event) => {
      listeners.forEach(({ wrappedHandler }) => {
        this.element.removeEventListener(event, wrappedHandler);
      });
    });
    this.listeners.clear();
  }
}

function getSiblings(element, filter, includeSelf = false) {
  let siblings = [];
  let currentElement = element;
  
  while (currentElement = currentElement.previousElementSibling) {
    if (!filter || currentElement.matches(filter)) {
      siblings.push(currentElement);
    }
  }
  
  currentElement = element;
  while (currentElement = currentElement.nextElementSibling) {
    if (!filter || currentElement.matches(filter)) {
      siblings.push(currentElement);
    }
  }
  
  if (includeSelf) {
    siblings.push(element);
  }
  
  return siblings;
}

// ===== CLASES BASE =====
var CustomHTMLElement = class extends HTMLElement {
  constructor() {
    super();
    this._hasSectionReloaded = false;
  }
  
  get rootDelegate() {
    return this._rootDelegate = this._rootDelegate || new Delegate(document.documentElement);
  }
  
  get delegate() {
    return this._delegate = this._delegate || new Delegate(this);
  }
  
  showLoadingBar() {
    triggerEvent(document.documentElement, "theme:loading:start");
  }
  
  hideLoadingBar() {
    triggerEvent(document.documentElement, "theme:loading:end");
  }
  
  untilVisible(intersectionObserverOptions = { rootMargin: "30px 0px", threshold: 0 }) {
    const onBecameVisible = () => {
      this.classList.add("became-visible");
      this.style.opacity = "1";
    };
    return new Promise((resolve) => {
      if (window.IntersectionObserver) {
        this.intersectionObserver = new IntersectionObserver((event) => {
          if (event[0].isIntersecting) {
            this.intersectionObserver.disconnect();
            requestAnimationFrame(() => {
              resolve();
              onBecameVisible();
            });
          }
        }, intersectionObserverOptions);
        this.intersectionObserver.observe(this);
      } else {
        resolve();
        onBecameVisible();
      }
    });
  }
  
  disconnectedCallback() {
    this.delegate.destroy();
    this.rootDelegate.destroy();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
};

var OpenableElement = class extends CustomHTMLElement {
  static get observedAttributes() {
    return ["open"];
  }
  
  constructor() {
    super();
  }
  
  connectedCallback() {
    this.delegate.on("click", ".openable__overlay", () => this.open = false);
    this.delegate.on("click", '[data-action="close"]', (event) => {
      event.stopPropagation();
      this.open = false;
    });
  }
  
  get requiresLoading() {
    return this.hasAttribute("href");
  }
  
  get open() {
    return this.hasAttribute("open");
  }
  
  set open(value) {
    if (value) {
      (async () => {
        await this._load();
        this.clientWidth;
        this.setAttribute("open", "");
      })();
    } else {
      this.removeAttribute("open");
    }
  }
  
  get shouldTrapFocus() {
    return true;
  }
  
  get returnFocusOnDeactivate() {
    return !this.hasAttribute("return-focus") || this.getAttribute("return-focus") === "true";
  }
  
  async _load() {
    if (this.requiresLoading && !this.hasLoaded) {
      this.showLoadingBar();
      try {
        const response = await fetch(this.getAttribute("href"));
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newContent = doc.querySelector(this.tagName.toLowerCase());
        if (newContent) {
          this.innerHTML = newContent.innerHTML;
        }
        this.hasLoaded = true;
      } catch (error) {
        console.error("Failed to load content:", error);
      } finally {
        this.hideLoadingBar();
      }
    }
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open") {
      if (this.open) {
        this._open();
      } else {
        this._close();
      }
    }
  }
  
  _open() {
    document.body.classList.add("lock-scroll");
    if (this.shouldTrapFocus) {
      this._trapFocus();
    }
  }
  
  _close() {
    document.body.classList.remove("lock-scroll");
    if (this.shouldTrapFocus) {
      this._releaseFocus();
    }
  }
  
  _trapFocus() {
    this.focusableElements = this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    
    this._handleKeyDown = (event) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === this.firstFocusableElement) {
            this.lastFocusableElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === this.lastFocusableElement) {
            this.firstFocusableElement.focus();
            event.preventDefault();
          }
        }
      }
    };
    
    document.addEventListener('keydown', this._handleKeyDown);
    this.firstFocusableElement?.focus();
  }
  
  _releaseFocus() {
    if (this._handleKeyDown) {
      document.removeEventListener('keydown', this._handleKeyDown);
    }
  }
};

// ===== CARGADOR DE LIBRERÍAS =====
var LibraryLoader = class {
  static load(libraryName) {
    
    const STATUS_REQUESTED = "requested";
    const STATUS_LOADED = "loaded";
    const library = this.libraries[libraryName];
    
    if (!library) {
      return Promise.resolve();
    }
    
    if (library.status === STATUS_REQUESTED) {
      return library.promise;
    }
    
    if (library.status === STATUS_LOADED) {
      return Promise.resolve();
    }

    let promise;
    
    if (library.type === "script") {
      promise = new Promise((resolve, reject) => {
        // Verificar si ya existe
        const existingTag = document.getElementById(library.tagId);
        if (existingTag) {
          library.status = STATUS_LOADED;
          resolve();
          return;
        }

        let tag = document.createElement("script");
        tag.id = library.tagId;
        tag.src = library.src;
        
        tag.onerror = (error) => {
          console.error(`[LibraryLoader] Error cargando script ${libraryName}:`, error);
          console.error(`[LibraryLoader] URL que falló: ${library.src}`);
          console.error(`[LibraryLoader] Error details:`, error);
          reject(error);
        };
        
        tag.onload = () => {
          library.status = STATUS_LOADED;
          resolve();
        };

        document.body.appendChild(tag);
      });
    } else {
      promise = new Promise((resolve, reject) => {
        // Verificar si ya existe
        const existingTag = document.getElementById(library.tagId);
        if (existingTag) {
          library.status = STATUS_LOADED;
          resolve();
          return;
        }

        let tag = document.createElement("link");
        tag.id = library.tagId;
        tag.href = library.src;
        tag.rel = "stylesheet";
        tag.type = "text/css";
        
        tag.onerror = (error) => {
          reject(error);
        };
        
        tag.onload = () => {
          library.status = STATUS_LOADED;
          resolve();
        };

        document.body.appendChild(tag);
      });
    }
    
    library.promise = promise;
    library.status = STATUS_REQUESTED;
    return promise;
  }
};

LibraryLoader.libraries = {
  flickity: {
    tagId: "flickity",
    src: window.themeVariables?.libs?.flickity || "/assets/flickity.min.js",
    type: "script"
  },
  photoswipe: {
    tagId: "photoswipe",
    src: window.themeVariables?.libs?.photoswipe || "/assets/photoswipe.min.js",
    type: "script"
  },
  modelViewerUiStyles: {
    tagId: "shopify-model-viewer-ui-styles",
    src: "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css",
    type: "link"
  }
};


// ===== DETECCIÓN DE CARACTERÍSTICAS =====
var MediaFeatures = class {
  static prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  
  static supportsHover() {
    return window.matchMedia("(pointer: fine)").matches;
  }
};

// ===== COMPONENTE FLICKITY CAROUSEL =====
var FlickityCarousel = class extends CustomHTMLElement {
  constructor() {
    super();
    if (this.childElementCount === 1) {
      return;
    }
    this.addEventListener("flickity:ready", this._preloadNextImage.bind(this));
    this.addEventListener("flickity:slide-changed", this._preloadNextImage.bind(this));
    this._createFlickity();
  }
  
  async disconnectedCallback() {
    if (this.flickity) {
      const flickityInstance = await this.flickity;
      flickityInstance.destroy();
    }
  }
  
  get flickityConfig() {
    return JSON.parse(this.getAttribute("flickity-config"));
  }
  
  get flickityInstance() {
    return this.flickity;
  }
  
  async next() {
    (await this.flickityInstance).next();
  }
  
  async previous() {
    (await this.flickityInstance).previous();
  }
  
  async select(indexOrSelector) {
    (await this.flickityInstance).selectCell(indexOrSelector);
  }
  
  async setDraggable(draggable) {
    const flickityInstance = await this.flickity;
    flickityInstance.options.draggable = draggable;
    flickityInstance.updateDraggable();
  }
  
  async reload() {
    const flickityInstance = await this.flickity;
    flickityInstance.destroy();
    if (this.flickityConfig["cellSelector"]) {
      Array.from(this.children).sort((a, b) => parseInt(a.getAttribute("data-original-position")) > parseInt(b.getAttribute("data-original-position")) ? 1 : -1).forEach((node) => this.appendChild(node));
    }
    this._createFlickity();
  }
  
  async _createFlickity() {
    this.flickity = new Promise(async (resolve) => {
      try {
        await LibraryLoader.load("flickity");

        await this.untilVisible({ rootMargin: "400px", threshold: 0 });
        
        // Verificar que Flickity esté disponible!!window.Flickity);
        if (!window.Flickity) {
          console.warn('[FlickityCarousel] Flickity no está disponible a través del LibraryLoader. Intentando cargar directamente...');
          await this._loadFlickityDirectly();
        }
        
        // Verificar nuevamente después del intento de carga directa !!window.Flickity);
        if (!window.Flickity) {
          console.error('Flickity no está disponible. Asegúrate de que la librería esté cargada correctamente.');
          resolve(null);
          return;
        }
        
        const flickityInstance = new window.Flickity(this, { 
          ...this.flickityConfig, 
          ...{
            rightToLeft: window.themeVariables?.settings?.direction === "rtl" || false,
            accessibility: MediaFeatures.supportsHover(),
            on: {
              ready: (event) => triggerEvent(this, "flickity:ready", event),
              change: (event) => {
                this._pauseAllMedia();
                triggerEvent(this, "flickity:slide-changed", event);
              },
              settle: (event) => triggerEvent(this, "flickity:slide-settled", event)
            }
          } 
        });
        
        resolve(flickityInstance);
      } catch (error) {
        console.error('Error creando Flickity:', error);
        resolve(null);
      }
    });
    
    if (this.hasAttribute("click-nav")) {
      const flickityInstance = await this.flickityInstance;
      if (flickityInstance) {
        flickityInstance.on("staticClick", this._onStaticClick.bind(this));
        this.addEventListener("mousemove", this._onMouseMove.bind(this));
      }
    }
  }
  
  async _loadFlickityDirectly() {
    return new Promise((resolve, reject) => {
      // Verificar si ya está cargado
      if (window.Flickity) {
        resolve();
        return;
      }

      // Intentar cargar desde CDN
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js';
      
      script.onload = () => {
        resolve();
      };
      
      script.onerror = (error) => {
        console.error('[FlickityCarousel] Error cargando Flickity desde CDN:', error);
        console.error('[FlickityCarousel] URL que falló:', script.src);
        reject(new Error('No se pudo cargar Flickity'));
      };

      document.head.appendChild(script);
    });
  }
  
  async _onStaticClick(event, pointer, cellElement) {
    const flickityInstance = await this.flickityInstance;
    if (!flickityInstance) return;
    
    const isVideoOrModelType = flickityInstance.selectedElement.hasAttribute("data-media-type") && 
      ["video", "external_video", "model"].includes(flickityInstance.selectedElement.getAttribute("data-media-type"));
    
    if (!cellElement || isVideoOrModelType || window.matchMedia("(max-width: 700px)").matches) {
      return;
    }
    
    const flickityViewport = flickityInstance.viewport;
    const boundingRect = flickityViewport.getBoundingClientRect();
    const halfEdge = Math.floor(boundingRect.right - boundingRect.width / 2);
    
    if (pointer.clientX > halfEdge) {
      flickityInstance.next();
    } else {
      flickityInstance.previous();
    }
  }
  
  async _onMouseMove(event) {
    const flickityInstance = await this.flickityInstance;
    if (!flickityInstance) return;
    
    const isVideoOrModelType = flickityInstance.selectedElement.hasAttribute("data-media-type") && 
      ["video", "external_video", "model"].includes(flickityInstance.selectedElement.getAttribute("data-media-type"));
    
    this.classList.toggle("is-hovering-right", event.offsetX > this.clientWidth / 2 && !isVideoOrModelType);
    this.classList.toggle("is-hovering-left", event.offsetX <= this.clientWidth / 2 && !isVideoOrModelType);
  }
  
  async _preloadNextImage() {
    const flickityInstance = await this.flickity;
    if (flickityInstance && flickityInstance.selectedElement.nextElementSibling) {
      flickityInstance.selectedElement.nextElementSibling.querySelector("img")?.setAttribute("loading", "eager");
    }
  }
  
  _pauseAllMedia() {
    // Método extraído del tema original - pausa todos los media al cambiar de slide
    const flickityInstance = this.flickity;
    if (flickityInstance) {
      flickityInstance.then(instance => {
        instance.cells.forEach((item) => {
          if (["external_video", "video", "model"].includes(item.element.getAttribute("data-media-type"))) {
            const mediaElement = item.element.firstElementChild;
            if (mediaElement && mediaElement.pause) {
              mediaElement.pause();
            }
          }
        });
      });
    }
  }
};

// ===== CONTROLES FLICKITY =====
var FlickityControls = class extends CustomHTMLElement {
  async connectedCallback() {
    this.flickityCarousel.addEventListener("flickity:ready", this._onSlideChanged.bind(this, false));
    this.flickityCarousel.addEventListener("flickity:slide-changed", this._onSlideChanged.bind(this, true));
    this.delegate.on("click", '[data-action="prev"]', () => this.flickityCarousel.previous());
    this.delegate.on("click", '[data-action="next"]', () => this.flickityCarousel.next());
    this.delegate.on("click", '[data-action="select"]', (event, target) => this.flickityCarousel.select(`#${target.getAttribute("aria-controls")}`));
  }
  
  get flickityCarousel() {
    return this._flickityCarousel = this._flickityCarousel || document.getElementById(this.getAttribute("controls"));
  }
  
  async _onSlideChanged(animate = true) {
    let flickityInstance = await this.flickityCarousel.flickityInstance;
    let activeItems = Array.from(this.querySelectorAll(`[aria-controls="${flickityInstance.selectedElement.id}"]`));
    
    activeItems.forEach((activeItem) => {
      activeItem.setAttribute("aria-current", "true");
      getSiblings(activeItem).forEach((sibling) => sibling.removeAttribute("aria-current"));
      
      requestAnimationFrame(() => {
        if (activeItem.offsetParent && activeItem.offsetParent !== this) {
          const windowHalfHeight = activeItem.offsetParent.clientHeight / 2;
          const windowHalfWidth = activeItem.offsetParent.clientWidth / 2;
          activeItem.offsetParent.scrollTo({
            behavior: animate ? "smooth" : "auto",
            top: activeItem.offsetTop - windowHalfHeight + activeItem.clientHeight / 2,
            left: activeItem.offsetLeft - windowHalfWidth + activeItem.clientWidth / 2
          });
        }
      });
    });
  }
};

// ===== VIDEO EXTERNO =====
var ExternalVideo = class extends CustomHTMLElement {
  constructor() {
    super();
    this.hasLoaded = false;
    (async () => {
      if (this.autoPlay) {
        await this.untilVisible({ rootMargin: "300px", threshold: 0 });
        this.play();
      } else {
        this.addEventListener("click", this.play.bind(this), { once: true });
      }
    })();
  }
  
  get autoPlay() {
    return this.hasAttribute("autoplay");
  }
  
  get provider() {
    return this.getAttribute("provider");
  }
  
  async play() {
    if (!this.hasLoaded) {
      await this._setupPlayer();
    }
    
    const iframe = this.querySelector("iframe");
    if (!iframe || !iframe.contentWindow) {
      return;
    }
    
    if (this.provider === "youtube") {
      setTimeout(() => {
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(JSON.stringify({ 
            event: "command", 
            func: "playVideo", 
            args: "" 
          }), "*");
        }
      }, 150);
    } else if (this.provider === "vimeo") {
      iframe.contentWindow.postMessage(JSON.stringify({ 
        method: "play" 
      }), "*");
    }
    
    triggerEvent(this, "video:played");
  }
  
  async pause() {
    if (!this.hasLoaded) {
      return;
    }
    
    const iframe = this.querySelector("iframe");
    if (!iframe || !iframe.contentWindow) {
      return;
    }
    
    if (this.provider === "youtube") {
      iframe.contentWindow.postMessage(JSON.stringify({ 
        event: "command", 
        func: "pauseVideo", 
        args: "" 
      }), "*");
    } else if (this.provider === "vimeo") {
      iframe.contentWindow.postMessage(JSON.stringify({ 
        method: "pause" 
      }), "*");
    }
    
    triggerEvent(this, "video:paused");
  }
  
  async _setupPlayer() {
    if (this._setupPromise) {
      return this._setupPromise;
    }
    
    return this._setupPromise = new Promise((resolve) => {
      const template = this.querySelector("template");
      if (template) {
        const node = template.content.firstElementChild.cloneNode(true);
        node.onload = () => {
          this.hasLoaded = true;
          resolve();
        };
        this.innerHTML = "";
        this.appendChild(node);
      } else {
        this.hasLoaded = true;
        resolve();
      }
    });
  }
};

// ===== VIDEO NATIVO =====
var NativeVideo = class extends HTMLElement {
  constructor() {
    super();
    this.hasLoaded = false;
    if (this.autoPlay) {
      this.play();
    } else {
      this.addEventListener("click", this.play.bind(this), { once: true });
    }
  }
  
  get autoPlay() {
    return this.hasAttribute("autoplay");
  }
  
  async play() {
    if (!this.hasLoaded) {
      await this._setupPlayer();
    }
    
    const video = this.querySelector("video");
    if (video) {
      video.play();
    }
    
    triggerEvent(this, "video:played");
  }
  
  async pause() {
    const video = this.querySelector("video");
    if (video) {
      video.pause();
    }
    
    triggerEvent(this, "video:paused");
  }
  
  async _setupPlayer() {
    const template = this.querySelector("template");
    if (template) {
      const node = template.content.firstElementChild.cloneNode(true);
      if (!this.hasAttribute("autoplay")) {
        this.innerHTML = "";
      }
      this.appendChild(node);
      this.firstElementChild.addEventListener("play", () => {
        triggerEvent(this, "video:played");
      });
      this.firstElementChild.addEventListener("pause", () => {
        triggerEvent(this, "video:paused");
      });
      this.hasLoaded = true;
    }
  }
};

// ===== MODELO 3D =====
var ModelMedia = class extends CustomHTMLElement {
  constructor() {
    super();
    this.hasLoaded = false;
    this.modelUi = null;
    this.modelViewerUIAvailable = false;
    
    // Cargar estilos de ModelViewerUI
    LibraryLoader.load("modelViewerUiStyles");
    
    // Usar Shopify.loadFeatures para cargar ModelViewerUI
    if (typeof window.Shopify !== 'undefined' && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            // Marcar que ModelViewerUI está disponible
            this.modelViewerUIAvailable = true;
            // Intentar inicializar si ya está cargado
            this._tryInitializeModelViewerUI();
          }
        }
      ]);
    }
    
    // Forzar la carga del modelo inmediatamente
    this._forceLoadModel();
    
    (async () => {
      if (this.autoPlay) {
        await this.untilVisible({ rootMargin: "300px", threshold: 0 });
        this.play();
      } else {
        this.addEventListener("click", this.play.bind(this), { once: true });
      }
    })();
  }
  
  get autoPlay() {
    return this.hasAttribute("autoplay");
  }
  
  _forceLoadModel() {
    // Verificar si ya tenemos el modelo renderizado directamente
    const modelViewer = this.querySelector("model-viewer");
    
    if (modelViewer && !this.hasLoaded) {
      this.hasLoaded = true;
      
      // Intentar inicializar ModelViewerUI después de cargar el contenido
      setTimeout(() => {
        this._tryInitializeModelViewerUI();
      }, 100);
    } else if (!modelViewer) {
      // Si no hay modelo, buscar template (comportamiento original)
      const template = this.querySelector("template");
      if (template && !this.hasLoaded) {
        this.innerHTML = template.innerHTML;
        this.hasLoaded = true;
        
        // Intentar inicializar ModelViewerUI después de cargar el contenido
        setTimeout(() => {
          this._tryInitializeModelViewerUI();
        }, 100);
      }
    }
  }
  
  async play() {
    if (!this.hasLoaded) {
      await this._setupPlayer();
    }
    
    if (this.modelUi) {
      this.modelUi.play();
      // Asegurar que los controles se muestren cuando se reproduce
      this._showControls();
    }
    
    triggerEvent(this, "model:played");
  }
  
  async pause() {
    if (this.modelUi) {
      this.modelUi.pause();
      this._hideControls();
    }
    
    triggerEvent(this, "model:paused");
  }
  
  async _setupPlayer() {
    // Si ya se cargó forzadamente, no hacer nada
    if (this.hasLoaded) {
      return;
    }
    
    const template = this.querySelector("template");
    if (template) {
      this.innerHTML = template.innerHTML;
      this.hasLoaded = true;
      
      // Intentar inicializar ModelViewerUI después de cargar el contenido
      setTimeout(() => {
        this._tryInitializeModelViewerUI();
      }, 100);
      
      // Disparar evento para notificar que el modelo está cargado
      triggerEvent(this, "model:loaded");
    }
  }
  
  _tryInitializeModelViewerUI() {
    // Solo intentar si ModelViewerUI está disponible y el contenido está cargado
    if (!this.modelViewerUIAvailable || !this.hasLoaded || this.modelUi) {
      return;
    }
    
    const modelViewer = this.querySelector("model-viewer");
    if (!modelViewer) {
      // Si no está disponible aún, reintentar después
      setTimeout(() => {
        this._tryInitializeModelViewerUI();
      }, 200);
      return;
    }
    
    this._initializeModelViewerUI(modelViewer);
  }
  
  _initializeModelViewerUI(modelViewer) {
    if (this.modelUi) {
      return; // Ya está inicializado
    }
    
    try {
      // Verificar que el modelo esté completamente cargado
      if (modelViewer.loaded) {
        this._createModelViewerUI(modelViewer);
      } else {
        // Esperar a que el modelo se cargue completamente
        modelViewer.addEventListener('load', () => {
          this._createModelViewerUI(modelViewer);
        }, { once: true });
        
        // También agregar un timeout como respaldo
        setTimeout(() => {
          if (!this.modelUi && modelViewer.loaded) {
            this._createModelViewerUI(modelViewer);
          }
        }, 1000);
      }
    } catch (error) {
    }
  }
  
  _createModelViewerUI(modelViewer) {
    if (this.modelUi) {
      return; // Ya está inicializado
    }
    
    try {
      this.modelUi = new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: false });
      
      modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => {
        this._showControls();
        modelViewer.dispatchEvent(new CustomEvent("model:played", { bubbles: true }));
      });
      modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => {
        this._hideControls();
        modelViewer.dispatchEvent(new CustomEvent("model:paused", { bubbles: true }));
      });
      
      // Forzar que el modelo inicie en modo "playing" para mostrar los controles
      setTimeout(() => {
        if (this.modelUi && modelViewer.ready) {
          this.modelUi.play();
          // Forzar que los controles se muestren
          this._showControls();
        }
      }, 100);
      
      // También intentar después de un tiempo más largo por si acaso
      setTimeout(() => {
        if (this.modelUi && !this._areControlsVisible()) {
          this.modelUi.play();
          this._showControls();
        }
      }, 500);
      
    } catch (error) {
    }
  }
  
  _showControls() {
    const controlsArea = this.querySelector('.shopify-model-viewer-ui__controls-area');
    if (controlsArea) {
      controlsArea.classList.add('shopify-model-viewer-ui__controls-area--playing');
    }
  }
  
  _hideControls() {
    const controlsArea = this.querySelector('.shopify-model-viewer-ui__controls-area');
    if (controlsArea) {
      controlsArea.classList.remove('shopify-model-viewer-ui__controls-area--playing');
    }
  }
  
  _areControlsVisible() {
    const controlsArea = this.querySelector('.shopify-model-viewer-ui__controls-area');
    return controlsArea && controlsArea.classList.contains('shopify-model-viewer-ui__controls-area--playing');
  }
  
  _autoPlayModel() {
    // Buscar el model-viewer dentro del elemento
    const modelViewer = this.querySelector("model-viewer");
    if (!modelViewer) return;
    
    // Intentar hacer click en el botón del poster dentro del shadow root
    const tryClickPoster = () => {
      try {
        // Acceder al shadow root del model-viewer
        const shadowRoot = modelViewer.shadowRoot;
        if (shadowRoot) {
          // Buscar el botón del poster
          const posterButton = shadowRoot.querySelector('#default-poster');
          if (posterButton) {
            // Hacer click programático
            posterButton.click();
            return true;
          }
        }
      } catch (error) {
        // El shadow root puede no estar disponible inmediatamente
      }
      return false;
    };
    
    // Intentar inmediatamente
    if (!tryClickPoster()) {
      // Si no funciona, intentar después de un delay
      setTimeout(() => {
        tryClickPoster();
      }, 100);
      
      // Y también después de un delay más largo como respaldo
      setTimeout(() => {
        tryClickPoster();
      }, 500);
    }
  }
  
  disconnectedCallback() {
    this.modelUi?.destroy();
  }
  
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
    } else {
      const productHandle = this.getAttribute("product-handle");
      if (productHandle) {
        try {
          // Cargar información del producto para XR
          const response = await fetch(`/products/${productHandle}.js`);
          const product = await response.json();
          const models = product.media.filter((media) => media.media_type === "model");
          window.ShopifyXR.addModels(models);
          window.ShopifyXR.setupXRElements();
        } catch (error) {
          console.error('[ModelMedia] Error cargando producto para XR:', error);
        }
      }
    }
  }
};


// ===== UI DE PHOTOSWIPE =====
var PhotoSwipeUi = class {
  constructor(pswp) {
    this.photoSwipeInstance = pswp;
    this.delegate = new Delegate(this.photoSwipeInstance.scrollWrap);
    this.maxSpreadZoom = window.themeVariables?.settings?.mobileZoomFactor || 2;
    this.pswpUi = this.photoSwipeInstance.scrollWrap.querySelector(".pswp__ui");

    this.delegate.on("click", '[data-action="pswp-close"]', this._close.bind(this));
    this.delegate.on("click", '[data-action="pswp-prev"]', this._goToPrev.bind(this));
    this.delegate.on("click", '[data-action="pswp-next"]', this._goToNext.bind(this));
    this.delegate.on("click", '[data-action="pswp-move-to"]', this._moveTo.bind(this));
    
    if (this.photoSwipeInstance) {
      this.photoSwipeInstance.listen("close", this._onPswpClosed.bind(this));
      this.photoSwipeInstance.listen("doubleTap", this._onPswpDoubleTap.bind(this));
      this.photoSwipeInstance.listen("beforeChange", this._onPswpBeforeChange.bind(this));
      this.photoSwipeInstance.listen("initialZoomInEnd", this._onPswpInitialZoomInEnd.bind(this));
      this.photoSwipeInstance.listen("initialZoomOut", this._onPswpInitialZoomOut.bind(this));
      this.photoSwipeInstance.listen("parseVerticalMargin", this._onPswpParseVerticalMargin.bind(this));
    } else {
      console.error('[PhotoSwipeUi] photoSwipeInstance no está disponible para registrar eventos');
    }
    // Registrar eventos de zoom (como en el código original)
    this.delegate.on("pswpTap", ".pswp__img", this._onPswpTap.bind(this));
  }
  
  init() {
    const prevNextButtons = this.pswpUi?.querySelector(".pswp__prev-next-buttons");
    const dotsNavWrapper = this.pswpUi?.querySelector(".pswp__dots-nav-wrapper");
    
    if (this.photoSwipeInstance.items.length <= 1) {
      if (prevNextButtons) prevNextButtons.style.display = "none";
      if (dotsNavWrapper) dotsNavWrapper.style.display = "none";
      return;
    }
    
    if (prevNextButtons) prevNextButtons.style.display = "";
    if (dotsNavWrapper) dotsNavWrapper.style.display = "";
    
    let dotsNavHtml = "";
    this.photoSwipeInstance.items.forEach((item, index) => {
      dotsNavHtml += `
        <button class="dots-nav__item tap-area" ${index === 0 ? 'aria-current="true"' : ""} data-action="pswp-move-to">
          <span class="visually-hidden">Go to slide ${index}</span>
        </button>
      `;
    });
    
    const dotsNav = dotsNavWrapper?.querySelector(".dots-nav");
    if (dotsNav) dotsNav.innerHTML = dotsNavHtml;
  }
  
  _close() {
    this.photoSwipeInstance.close();
  }
  
  _goToPrev() {
    this.photoSwipeInstance.prev();
  }
  
  _goToNext() {
    this.photoSwipeInstance.next();
  }
  
  _moveTo(event, target) {
    this.photoSwipeInstance.goTo([...target.parentNode.children].indexOf(target));
  }
  
  _onPswpClosed() {
    this.delegate.off("pswpTap");
    
    // Cerrar el elemento product-image-zoom usando el setter (como en el tema original)
    const productImageZoom = this.photoSwipeInstance.element;
    
    // Intentar también buscar el elemento desde el DOM
    const productImageZoomFromDOM = document.querySelector('product-image-zoom[open]');

    if (productImageZoom) {
      productImageZoom.open = false;
    }
    
    // Si el primer método no funcionó, intentar con el elemento del DOM
    if (productImageZoomFromDOM && productImageZoomFromDOM !== productImageZoom) {
      productImageZoomFromDOM.open = false;
    }
    
    // Actualizar el botón de zoom para que tenga aria-expanded="false"
    const zoomButton = document.querySelector('.product__zoom-button');
    if (zoomButton) {
      zoomButton.setAttribute('aria-expanded', 'false');
    }
  }
  
  _onPswpDoubleTap(point) {
    const initialZoomLevel = this.photoSwipeInstance.currItem.initialZoomLevel;
    const currentZoomLevel = this.photoSwipeInstance.getZoomLevel();
    
    if (currentZoomLevel !== initialZoomLevel) {
    } else {
      const targetZoom = initialZoomLevel < 0.7 ? 1 : this.maxSpreadZoom;
      this.photoSwipeInstance.zoomTo(targetZoom, point, 333);
    }
  }
  
  _onPswpTap(event) {
    
    // Implementación exacta del código original
    if (event.detail.pointerType === "mouse") {
      this.photoSwipeInstance.toggleDesktopZoom(event.detail.releasePoint);
    }
  }
  
  _onPswpBeforeChange() {
    if (this.photoSwipeInstance.items.length <= 1) return;
    
    const activeDot = this.photoSwipeInstance.scrollWrap.querySelector(`.dots-nav__item:nth-child(${this.photoSwipeInstance.getCurrentIndex() + 1})`);
    if (activeDot) {
      activeDot.setAttribute("aria-current", "true");
      getSiblings(activeDot).forEach((item) => item.removeAttribute("aria-current"));
    }
  }
  
  _onPswpInitialZoomInEnd() {
    this.pswpUi?.classList.remove("pswp__ui--hidden");
  }
  
  _onPswpInitialZoomOut() {
    this.pswpUi?.classList.add("pswp__ui--hidden");
  }
  
  _onPswpParseVerticalMargin(item) {
    // Verificar que photoSwipeInstance y items estén disponibles
    if (!this.photoSwipeInstance || !this.photoSwipeInstance.items) {
      console.warn('[PhotoSwipeUi] photoSwipeInstance o items no disponibles en _onPswpParseVerticalMargin');
      return;
    }
    
    // Verificar que item y vGap estén disponibles
    if (!item || !item.vGap) {
      console.warn('[PhotoSwipeUi] item o vGap no disponibles en _onPswpParseVerticalMargin');
      return;
    }
    
    const itemsLength = this.photoSwipeInstance.items.length;
    const isDesktop = window.matchMedia("(min-width: 700px)").matches;
    const bottomMargin = itemsLength <= 1 || isDesktop ? 0 : 60;

    item.vGap.bottom = bottomMargin;
  }
};

// ===== ZOOM DE IMAGEN =====
var ProductImageZoom = class extends OpenableElement {
  connectedCallback() {
    super.connectedCallback();
    this.mediaElement = this.closest(".product__media");
    this.maxSpreadZoom = window.themeVariables?.settings?.mobileZoomFactor || 2;

    LibraryLoader.load("photoswipe");
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    this.photoSwipeInstance?.destroy();
  }
  
  
  async attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case "open":
        if (this.open) {
          await LibraryLoader.load("photoswipe");
          this._openPhotoSwipe();
        }
        break;
    }
  }
  
  async _openPhotoSwipe() {
    // Verificar que PhotoSwipe esté disponible (puede ser PhotoSwipe o ThemePhotoSwipe)
    const PhotoSwipeClass = window.PhotoSwipe || window.ThemePhotoSwipe;
    if (!PhotoSwipeClass) {
      console.error('[ProductImageZoom] PhotoSwipe no está disponible. Verifica que la librería se haya cargado correctamente.');
      console.error('[ProductImageZoom] window.PhotoSwipe:', !!window.PhotoSwipe);
      console.error('[ProductImageZoom] window.ThemePhotoSwipe:', !!window.ThemePhotoSwipe);
      return;
    }
    
    // Filtrar solo elementos de tipo imagen (como en el código original)
    const activeImages = Array.from(this.mediaElement.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)'));
    
    if (activeImages.length === 0) {
      return;
    }
    
    const currentMediaIndex = activeImages.findIndex(item => item.classList.contains("is-selected"));
    
    if (currentMediaIndex === -1) {
      return;
    }
    
    const items = activeImages.map(item => {
      const img = item.querySelector("img");
      
      // Verificar que la imagen esté cargada
      if (!img || !img.src) {
        console.warn('[ProductImageZoom] Imagen no válida encontrada:', img);
        return null;
      }
      
      // Si la imagen no está completamente cargada, usar dimensiones por defecto
      const width = img.naturalWidth || img.width || 800;
      const height = img.naturalHeight || img.height || 600;
      
      return {
        src: img.src,
        width: width,
        height: height,
        alt: img.alt || '',
        // Propiedades adicionales para PhotoSwipe
        w: width,
        h: height,
        // Agregar propiedades que PhotoSwipe puede necesitar
        msrc: img.src, // imagen pequeña (misma que la principal por ahora)
        title: img.alt || ''
      };
    }).filter(item => item !== null); // Filtrar items nulos
    
    // Verificar que tenemos items válidos
    if (items.length === 0) {
      return;
    }
    
    // Verificar que todos los items tengan las propiedades necesarias
    const invalidItems = items.filter(item => !item.src || !item.width || !item.height);
    if (invalidItems.length > 0) {
      return;
    }
    
    const options = {
      index: currentMediaIndex,
      bgOpacity: 1,
      showHideOpacity: true,
      closeOnVerticalDrag: false,
      maxSpreadZoom: this.maxSpreadZoom,
      loop: false,
      allowPanToNext: false,
      closeOnScroll: false,
      getDoubleTapZoom: (isMouseClick, item) => {
        if (isMouseClick) {
          return item.w > item.h ? 1.6 : 1;
        } else {
          return item.initialZoomLevel < 0.7 ? 1 : 1.33;
        }
      }
    };

    
    // PhotoSwipe necesita 4 parámetros: elemento, UI, items, options
    // Usar el elemento product-image-zoom mismo (this) en lugar de .product__media
    this.photoSwipeInstance = new PhotoSwipeClass(this, PhotoSwipeUi, items, options);
    this.photoSwipeInstance.init();
    
    // Actualizar el botón de zoom para que tenga aria-expanded="true"
    const zoomButton = document.querySelector('.product__zoom-button');
    if (zoomButton) {
      zoomButton.setAttribute('aria-expanded', 'true');
    }
  }
};

// ===== MEDIA DE PRODUCTO =====
var ProductMedia = class extends CustomHTMLElement {
  async connectedCallback() {
    this.mainCarousel = this.querySelector("flickity-carousel");
    if (this.hasAttribute("reveal-on-scroll")) {
      this._setupVisibility();
    }
    if (this.mainCarousel.childElementCount === 1) {
      return;
    }
    
    const form = document.getElementById(this.getAttribute("form-id"));
    if (form) {
      form.addEventListener("variant:change", this._onVariantChanged.bind(this));
    }
    
    // También escuchar el evento variant::changed que se dispara desde global.js
    const variantRadios = document.querySelector('variant-radios');
    if (variantRadios) {
      variantRadios.addEventListener("variant::changed", this._onVariantChanged.bind(this));
    }
    
    this.mainCarousel.addEventListener("model:played", () => this.mainCarousel.setDraggable(false));
    this.mainCarousel.addEventListener("model:paused", () => this.mainCarousel.setDraggable(true));
    this.mainCarousel.addEventListener("video:played", () => this.mainCarousel.setDraggable(false));
    this.mainCarousel.addEventListener("video:paused", () => this.mainCarousel.setDraggable(true));
    this.mainCarousel.addEventListener("flickity:ready", this._onFlickityReady.bind(this));
    this.mainCarousel.addEventListener("flickity:slide-changed", this._onFlickityChanged.bind(this));
    this.mainCarousel.addEventListener("flickity:slide-settled", this._onFlickitySettled.bind(this));
    
    
    this._onFlickityReady();
  }
  
  
  async _onVariantChanged(event) {
    try {
      // Manejar diferentes tipos de eventos de variantes
      let variant;
      if (event.detail && event.detail.variant) {
        // Evento variant:change
        variant = event.detail.variant;
      } else if (event.target && event.target.currentVariant) {
        // Evento variant::changed
        variant = event.target.currentVariant;
      } else {
        return;
      }
      
      if (!variant) return;
      
      const filteredIds = JSON.parse(this.getAttribute("filtered-ids") || "[]");
      const mediaItems = Array.from(this.mainCarousel.children);
      
      mediaItems.forEach(item => {
        const mediaId = item.getAttribute("data-media-id");
        const shouldHide = filteredIds.includes(mediaId);
        item.classList.toggle("is-filtered", shouldHide);
      });
      
      // Solo recargar si hay elementos filtrados que requieren reorganización
      const hasFilteredItems = mediaItems.some(item => item.classList.contains('is-filtered'));
      if (hasFilteredItems) {
        await this.mainCarousel.reload();
      }
      
      // Cambiar a la imagen correspondiente de la variante
      if (variant.featured_media) {
        const featuredMediaId = variant.featured_media.id;
        
        // Obtener la instancia de Flickity antes de buscar el slide
        const flickityInstance = await this.mainCarousel.flickityInstance;
        
        // Verificar que flickityInstance existe
        if (!flickityInstance) {
          console.warn('[ProductMedia] flickityInstance no disponible para cambiar a variante');
          return;
        }
        
        // Intentar cambiar directamente usando el selector de Flickity
        try {
          flickityInstance.selectCell(`[data-media-id="${featuredMediaId}"]`);
          return; // Salir temprano si funcionó
        } catch (error) {
          // Si el selector directo falló, buscar por índice
        }
        
        // Si el selector directo falló, buscar por índice
        const targetSlide = this.mainCarousel.querySelector(`[data-media-id="${featuredMediaId}"]`);
        
        if (targetSlide && !targetSlide.classList.contains('is-filtered')) {
          // Buscar el índice usando el ID del media
          const slideIndex = Array.from(this.mainCarousel.children).findIndex(child => 
            child.getAttribute('data-media-id') === featuredMediaId.toString()
          );
          
          if (slideIndex !== -1) {
            flickityInstance.selectCell(slideIndex);
          }
        }
      }
    } catch (error) {
      console.error('[ProductMedia] Error en _onVariantChanged:', error);
    }
  }
  
  async _onFlickityReady() {
    try {
      const flickityInstance = await this.mainCarousel.flickityInstance;
      
      // Verificar que flickityInstance existe antes de usarlo
      if (!flickityInstance) {
        console.warn('[ProductMedia] flickityInstance no está disponible en _onFlickityReady');
        return;
      }
      
      // Verificar que selectedElement existe
      if (!flickityInstance.selectedElement) {
        console.warn('[ProductMedia] selectedElement no está disponible');
        return;
      }
      
      if (["video", "external_video"].includes(flickityInstance.selectedElement.getAttribute("data-media-type")) && this.hasAttribute("autoplay-video")) {
        flickityInstance.selectedElement.firstElementChild?.play();
      }
      
      // Manejar el botón de zoom inicial
      const selectedSlide = flickityInstance.selectedElement;
      const zoomButton = this.querySelector(".product__zoom-button");
      if (zoomButton && selectedSlide) {
        const mediaType = selectedSlide.getAttribute("data-media-type");
        
        // Ocultar el botón de zoom si el tipo de media no es "image"
        zoomButton.hidden = mediaType !== "image";
      }
      
      this._updateThumbnailScroll();
    } catch (error) {
      console.error('[ProductMedia] Error en _onFlickityReady:', error);
    }
  }
  
  _onFlickityChanged() {
    // Pausar todos los media al cambiar de slide (extraído del tema original)
    const flickityInstance = this.mainCarousel.flickity;
    if (flickityInstance) {
      flickityInstance.then(instance => {
        // Verificar que la instancia existe
        if (!instance || !instance.cells) {
          console.warn('[ProductMedia] instancia de Flickity no válida en _onFlickityChanged');
          return;
        }
        
        // Pausar todos los media primero
        instance.cells.forEach((item) => {
          if (item.element && ["external_video", "video", "model"].includes(item.element.getAttribute("data-media-type"))) {
            const mediaElement = item.element.firstElementChild;
            if (mediaElement && mediaElement.pause) {
              mediaElement.pause();
            }
          }
        });
        
        // Verificar que selectedElement existe
        if (!instance.selectedElement) {
          return;
        }
        
        // Reproducir automáticamente el media seleccionado si es video y tiene autoplay
        const selectedSlide = instance.selectedElement;
        const mediaType = selectedSlide.getAttribute("data-media-type");
        
        switch (mediaType) {
          case "model":
            // Para modelos 3D, buscar el elemento model-media y reproducir automáticamente
            const modelMedia = selectedSlide.querySelector("model-media");
            if (modelMedia && typeof modelMedia._autoPlayModel === 'function') {
              modelMedia._autoPlayModel();
            }
            break;
          case "external_video":
          case "video":
            if (this.hasAttribute("autoplay-video")) {
              selectedSlide.firstElementChild?.play();
            }
            break;
        }
      }).catch(error => {
        console.error('[ProductMedia] Error en _onFlickityChanged:', error);
      });
    }
    
    this._updateThumbnailScroll();
  }
  
  async _onFlickitySettled() {
    try {
      // Obtener la instancia de Flickity y el slide seleccionado
      const flickityInstance = await this.mainCarousel.flickityInstance;
      
      // Verificar que flickityInstance existe
      if (!flickityInstance) {
        console.warn('[ProductMedia] flickityInstance no está disponible en _onFlickitySettled');
        return;
      }
      
      // Verificar que selectedElement existe
      if (!flickityInstance.selectedElement) {
        console.warn('[ProductMedia] selectedElement no está disponible en _onFlickitySettled');
        return;
      }
      
      const selectedSlide = flickityInstance.selectedElement;
      
      // Manejar el botón de zoom
      const zoomButton = this.querySelector(".product__zoom-button");
      if (zoomButton && selectedSlide) {
        const mediaType = selectedSlide.getAttribute("data-media-type");
        
        // Ocultar el botón de zoom si el tipo de media no es "image"
        zoomButton.hidden = mediaType !== "image";
      }
      
      this._updateThumbnailScroll();
    } catch (error) {
      console.error('[ProductMedia] Error en _onFlickitySettled:', error);
    }
  }
  
  _updateThumbnailScroll() {
    const activeThumbnail = this.querySelector('[aria-current="true"]');
    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({ 
        behavior: "smooth", 
        block: "nearest", 
        inline: "center" 
      });
    }
  }
  
  _setupVisibility() {
    this.untilVisible({ rootMargin: "50px", threshold: 0 }).then(() => {
      this.classList.add("became-visible");
    });
  }
};

// ===== MIXIN LOADER BUTTON =====
var LoaderButtonMixin = {
  _prepareButton() {
    this.originalContent = this.innerHTML;
    this._startTransitionPromise = null;
    this.innerHTML = `
      <span class="loader-button__text">${this.innerHTML}</span>
      <span class="loader-button__loader" hidden>
        <div class="spinner">
          <svg focusable="false" width="24" height="24" class="icon icon--spinner" viewBox="25 25 50 50">
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
          </svg>
        </div>
      </span>
    `;
    this.textElement = this.firstElementChild;
    this.spinnerElement = this.lastElementChild;
    window.addEventListener("pagehide", () => this.removeAttribute("aria-busy"));
  },
  _startTransition() {
    const textAnimation = this.textElement.animate({
      opacity: [1, 0],
      transform: ["translateY(0)", "translateY(-10px)"]
    }, {
      duration: 75,
      easing: "ease",
      fill: "forwards"
    });
    this.spinnerElement.hidden = false;
    const spinnerAnimation = this.spinnerElement.animate({
      opacity: [0, 1],
      transform: ["translate(-50%, 0%)", "translate(-50%, -50%)"]
    }, {
      duration: 75,
      delay: 75,
      easing: "ease",
      fill: "forwards"
    });
    this._startTransitionPromise = Promise.all([
      new Promise((resolve) => textAnimation.onfinish = () => resolve()),
      new Promise((resolve) => spinnerAnimation.onfinish = () => resolve())
    ]);
  },
  async _endTransition() {
    if (!this._startTransitionPromise) {
      return;
    }
    await this._startTransitionPromise;
    this.spinnerElement.animate({
      opacity: [1, 0],
      transform: ["translate(-50%, -50%)", "translate(-50%, -100%)"]
    }, {
      duration: 75,
      easing: "ease",
      fill: "forwards"
    });
    this.textElement.animate({
      opacity: [0, 1],
      transform: ["translateY(10px)", "translateY(0)"]
    }, {
      duration: 75,
      delay: 75,
      easing: "ease",
      fill: "forwards"
    });
    this._startTransitionPromise = null;
  }
};

// ===== TOGGLE BUTTON =====
var ToggleButton = class extends HTMLButtonElement {
  static get observedAttributes() {
    return ["aria-expanded", "aria-busy"];
  }
  
  constructor() {
    super();
    if (this.hasAttribute("loader")) {
      this._prepareButton();
    }
    this.addEventListener("click", this._onButtonClick.bind(this));
    this.rootDelegate = new Delegate(document.documentElement);
  }
  
  _onButtonClick() {
    this.isExpanded = !this.isExpanded;
  }
  
  connectedCallback() {
    document.addEventListener("openable-element:close", (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = false;
        event.stopPropagation();
      }
    });
    document.addEventListener("openable-element:open", (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = true;
        event.stopPropagation();
      }
    });
    this.rootDelegate.on("openable-element:load:start", `#${this.getAttribute("aria-controls")}`, () => {
      if (this.classList.contains("button")) {
        this.setAttribute("aria-busy", "true");
      } else if (this.offsetParent !== null) {
        triggerEvent(document.documentElement, "theme:loading:start");
      }
    }, true);
    this.rootDelegate.on("openable-element:load:end", `#${this.getAttribute("aria-controls")}`, () => {
      if (this.classList.contains("button")) {
        this.removeAttribute("aria-busy");
      } else if (this.offsetParent !== null) {
        triggerEvent(document.documentElement, "theme:loading:end");
      }
    }, true);
  }
  
  disconnectedCallback() {
    this.rootDelegate.destroy();
  }
  
  get isExpanded() {
    return this.getAttribute("aria-expanded") === "true";
  }
  
  set isExpanded(value) {
    this.setAttribute("aria-expanded", value ? "true" : "false");
  }
  
  get controlledElement() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "aria-expanded":
        if (oldValue === "false" && newValue === "true") {
          this.controlledElement.open = true;
        } else if (oldValue === "true" && newValue === "false") {
          this.controlledElement.open = false;
        }
        break;
      case "aria-busy":
        if (this.hasAttribute("loader")) {
          if (newValue === "true") {
            this._startTransition();
          } else {
            this._endTransition();
          }
        }
        break;
    }
  }
};

Object.assign(ToggleButton.prototype, LoaderButtonMixin);

// ===== REGISTRO DE COMPONENTES =====
window.customElements.define("flickity-carousel", FlickityCarousel);
window.customElements.define("flickity-controls", FlickityControls);
window.customElements.define("external-video", ExternalVideo);
window.customElements.define("native-video", NativeVideo);
window.customElements.define("model-media", ModelMedia);
window.customElements.define("product-image-zoom", ProductImageZoom);
window.customElements.define("product-media", ProductMedia);
window.customElements.define("toggle-button", ToggleButton, { extends: "button" });


// ===== INICIALIZACIÓN =====
document.addEventListener('DOMContentLoaded', function() {
  // Asegurar que las variables del tema estén disponibles
  if (!window.themeVariables) {
    window.themeVariables = {
      settings: {
        direction: 'ltr',
        mobileZoomFactor: 2.5
      },
      libs: {
        flickity: "https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js",
        photoswipe: "https://unpkg.com/photoswipe@5/dist/photoswipe.umd.min.js"
      },
      breakpoints: {
        phone: 'screen and (max-width: 700px)'
      }
    };
  }

  // Solución para el botón de zoom en Safari
  document.addEventListener('click', function(event) {
    if (event.target.closest('.tap-area.product__zoom-button')) {
      const zoomButton = event.target.closest('.tap-area.product__zoom-button');
      const controlledElementId = zoomButton?.getAttribute('aria-controls');
      
      if (controlledElementId) {
        const controlledElement = document.getElementById(controlledElementId);
        
        if (controlledElement) {
          // Verificar si el ToggleButton está funcionando
          const isToggleButtonWorking = zoomButton?.isExpanded !== undefined;
          
          if (!isToggleButtonWorking) {
            // Prevenir el comportamiento por defecto del ToggleButton
            event.preventDefault();
            event.stopPropagation();
            
            // Toggle manual del modal
            const isCurrentlyOpen = controlledElement.open;
            controlledElement.open = !isCurrentlyOpen;
            
            // Actualizar aria-expanded del botón
            zoomButton.setAttribute('aria-expanded', !isCurrentlyOpen ? 'true' : 'false');
          }
        }
      }
    }
  });
});
